/**
 * Central business configuration.
 *
 * Every factual detail about the firm lives here so real values can be filled
 * in from one place. Values wrapped in [BRACKETS] are intentional placeholders
 * -- they have NOT been supplied and must not be guessed.
 *
 * ---------------------------------------------------------------------------
 * COPY GUARDRAILS (see README, "Copy guardrails")
 *
 * The published copy describes outcomes and services. It never describes how
 * the assistant is built or where it runs. Keep these words and subjects out
 * of every string in src/: model names and providers, vector stores,
 * embeddings, retrieval, database engines, serverless/service names,
 * "multi-tenant", internal architecture, and any URL that points at a service
 * endpoint. Endpoints belong in .env files, never in copy or markup.
 *
 * Also never publish: testimonials, client names, logos, certifications,
 * partner status, prices, savings figures, response-time metrics, or dates
 * that have not been confirmed.
 * ---------------------------------------------------------------------------
 */

/**
 * Canonical origin, used for the canonical link, Open Graph tags, and the
 * sitemap. Overridable per environment with REACT_APP_SITE_URL, which is
 * useful while the site is served from a CloudFront domain.
 */
const SITE_URL = process.env.REACT_APP_SITE_URL || 'https://www.qaaslab.com';

export const businessInfo = {
  /** Display name used in the header, footer, and metadata. */
  name: 'QAAS Lab',
  nameShort: 'QAAS Lab',
  /** Legal entity name for the footer imprint. Confirm the registered name. */
  legalName: '[LEGAL ENTITY NAME]',

  tagline: 'AI enablement for small and mid-sized businesses',
  /** The one-line promise, used in the footer and metadata descriptions. */
  primaryMessage: 'We build it, we host it, we run it for you.',

  /** --- Contact details --- */
  /**
   * Subject pre-filled by "email us" links, so an inquiry that arrives outside
   * the website form is still recognisable and threads together.
   */
  emailInquirySubject: 'Website inquiry',
  /**
   * Phone is stored with the country code so `getPhoneHref` can build a valid
   * international tel: link; `phoneDisplay` is the human-readable form used in
   * the UI. Both values are the ones published on the previous qaaslab.com
   * site -- confirm before launch.
   */
  phone: '+1 (949) 943-4868',
  phoneDisplay: '+1 (949) 943-4868',
  email: 'info@qaaslab.com',

  /** --- Coverage and availability --- */
  serviceAreaLong:
    'Engagements are delivered remotely, with on-site work when the engagement calls for it.',

  /** --- Domain / canonical URL used for SEO tags --- */
  siteUrl: SITE_URL,
};

/**
 * Builds a usable `tel:` link, or returns null when the number is still a
 * placeholder or is unusable. UI components render plain text in that case
 * rather than a dead link.
 */
export const getPhoneHref = () => {
  const digits = String(businessInfo.phone).replace(/[^\d+]/g, '');
  if (!/^\+?\d{7,}$/.test(digits)) return null;
  return `tel:${digits}`;
};

/** Human-readable phone number, or the raw value when no display form is set. */
export const getPhoneDisplay = () =>
  businessInfo.phoneDisplay || businessInfo.phone;

/**
 * Builds a usable `mailto:` link, or returns null while the address is a
 * placeholder. Pass a subject to pre-fill the visitor's mail client.
 */
export const getEmailHref = (subject) => {
  const value = String(businessInfo.email);
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return null;
  if (!subject) return `mailto:${value}`;
  return `mailto:${value}?subject=${encodeURIComponent(subject)}`;
};

/** True when a real legal entity name has been supplied. */
export const hasLegalName = () =>
  Boolean(businessInfo.legalName) && !businessInfo.legalName.startsWith('[');

export default businessInfo;
